import { FC, Suspense, useState, useEffect } from 'react';
import { DocumentPreview } from './DocumentPreview';
import { DriveItem } from '@microsoft/microsoft-graph-types';
import { Loader } from '../loading/Loader';
import { SectionErrorBoundary } from '../error/SectionErrorBoundary';
import { SectionHeader } from '../page/SectionHeader';
import { IconType } from 'react-icons';
import { TextInput } from '../form/TextInput';
import { FaSearch } from 'react-icons/fa';
import InfiniteScroll from 'react-infinite-scroller';
import strings from './onedrive.translations';
import { isFileFavourite } from '../../utils/favourite';

interface DocumentListProps extends DocumentListContentsProps {
  title: string;
  Icon: IconType;
  filterable?: boolean;
  customComponent?: JSX.Element;
}

interface DocumentListContentsProps {
  getDocuments: () => {
    files: DriveItem[];
    refetch: () => void;
  };
  includeCategories?: boolean;
  deleteable?: boolean;
  updateable?: boolean;
  filterTerm?: string;
}

const GRID_COLUMNS = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10';
const RESULTS_PER_PAGE = 8;

const fileFilter = (filterTerm?: string) => (file: DriveItem) => {
  return !filterTerm || file.name?.toLowerCase().includes(filterTerm.toLowerCase());
};

const fileSort = (fileA: DriveItem, fileB: DriveItem) => {
  const isAFave = isFileFavourite(fileA);
  const isBFave = isFileFavourite(fileB);

  if (isAFave && !isBFave) {
    return -1;
  }

  if (isBFave && !isAFave) {
    return 1;
  }

  if (fileA.name === fileB.name) {
    return 0;
  }

  return (fileA.name || '') > (fileB.name || '') ? 1 : -1;
}

const DocumentListContents: FC<DocumentListContentsProps> = ({
  getDocuments,
  includeCategories,
  deleteable,
  updateable,
  filterTerm
}) => {
  const { files, refetch } = getDocuments();

  const [filteredFiles, setFilteredFiles] = useState<DriveItem[]>(files.filter(fileFilter(filterTerm)));
  const [filesToShow, setFilesToShow] = useState<DriveItem[]>(filteredFiles.slice(0, RESULTS_PER_PAGE));

  useEffect(() => {
    const newFilteredFiles = files.filter(fileFilter(filterTerm)).sort(fileSort);

    setFilteredFiles(newFilteredFiles);
    setFilesToShow(newFilteredFiles.slice(0, RESULTS_PER_PAGE))
  }, [setFilteredFiles, setFilesToShow, filterTerm, files]);

  const loadMoreResults = () => {
    const page = Math.floor(filesToShow.length / RESULTS_PER_PAGE);
    setFilesToShow([...filesToShow, ...filteredFiles.slice(page * RESULTS_PER_PAGE, (page + 1) * RESULTS_PER_PAGE)]);
  };

  if (filteredFiles.length === 0) {
    return (
      <div className="flex items-center justify-center p-5">
        {files.length === 0 || !filterTerm
          ? strings.documentList.noDocuments
          : strings.documentList.noDocumentsMatch(filterTerm)}
      </div>
    );
  } else {
    return (
      <InfiniteScroll loadMore={loadMoreResults} hasMore={filesToShow.length < filteredFiles.length}>
        <div className={GRID_COLUMNS}>
          {filesToShow.map(file => (
            <DocumentPreview
              file={file}
              key={file.id}
              showCategory={includeCategories}
              onDelete={deleteable ? refetch : undefined}
              onUpdate={updateable ? refetch : undefined}
            />
          ))}
        </div>
      </InfiniteScroll>
    );
  }
};

const DocumentListLoader: FC = () => {
  return (
    <div className="p-5 w-full">
      <Loader className="h-10 w-10 mx-auto" />
    </div>
  );
};

export const DocumentListSection: FC<DocumentListProps> = ({ title, Icon, filterable, customComponent, ...props }) => {
  const [filterTerm, setFilterTerm] = useState<string | undefined>(filterable ? '' : undefined);

  return (
    <div>
      <div className="flex items-stretch sm:items-center w-full gap-2 pb-5 flex-col sm:flex-row">
        <SectionHeader className="flex-1" title={title} Icon={Icon}></SectionHeader>
        <div className="flex items-stretch gap-2">
          {filterable && (
            <TextInput
              value={filterTerm}
              onChange={setFilterTerm}
              Icon={FaSearch}
              clearable
              placeholder={strings.documentList.filterPlaceholder}
              containerClassName="w-full"
            />
          )}
          {customComponent}
        </div>
      </div>
      <Suspense fallback={<DocumentListLoader />}>
        <SectionErrorBoundary>
          <div>
            <DocumentListContents {...props} filterTerm={filterTerm} />
          </div>
        </SectionErrorBoundary>
      </Suspense>
    </div>
  );
};
